import React, { ReactNode, useContext, useMemo } from 'react'

import { ThemeProvider, useTheme } from '@emotion/react'
import whatInput from 'what-input'

import { ArcadeIconProvider } from '@arcade/web/icons/react'

import {
  ArcadeThemeColors,
  getArcadeColorsByThemeVariant,
  getOverrideArcadeColorsByThemeVariant,
} from '../theme/arcadeColors'
import border from '../theme/border'
import breakpoints from '../theme/breakpoints'
import {
  addOpacity,
  Colors,
  getColorsByThemeVariant,
  getOverrideColorsByThemeVariant,
  ThemeColors,
  ThemeVariant,
} from '../theme/colors'
import getDropShadows, { ThemeDropShadows } from '../theme/dropShadows'
import maxWidths from '../theme/maxWidths'
import motion from '../theme/motion'
import selectors from '../theme/selectors'
import spacing from '../theme/spacing'
import typography, { pxToRem, remToPx, remToPxRaw } from '../theme/typography'
import weights from '../theme/weights'
import { ArcadeComponentMigrationProvider } from './ArcadeComponentMigration/ArcadeComponentMigrationProvider'
import GlobalStyles from './GlobalStyles'

const ESC_CODE = 27
whatInput.ignoreKeys([ESC_CODE])

export type ImageUrlHandler = (args: { url: string; width: number }) => string | undefined
interface Props {
  themeVariant?: ThemeVariant
  darkMode: boolean
  children: React.ReactNode
  bodyBackground?: Colors
  /**
   * Arcade component migration feature flags to be passed down to the ArcadeMigrationProvider
   */
  featureFlags?: Record<string, string>
  /**
   * We are migrating to a new black and white background system.
   * This prop allows us to opt into using the new background system.
   */
  shouldUseArcadeBackground?: boolean
  /**
   * Black and white background system for the login page only.
   * This is separate from the `shouldUseArcadeBackground` prop to allow for
   * the login page to be opted-in and out separately from the rest of the application.
   */
  shouldUseArcadeBackgroundLogin?: boolean
  /**
   * Currently required for the arcade Avatar component to replace the image URL with a CDN URL.
   * This should be used in all components that accepts an image URL.
   */
  imageUrlHandler?: ImageUrlHandler
}

const ThemedArcadeIconProvider = ({ children }: { children: ReactNode }) => {
  const theme = useTheme()
  return (
    <ArcadeIconProvider
      variants={{
        prominent: theme.colors.iconProminent,
        standard: theme.colors.icon,
        subtle: theme.colors.iconSubtle,
        'extra-subtle': theme.colors.iconExtraSubtle,
        inverse: theme.colors.iconInverse,
        disabled: theme.colors.iconDisabled,
        danger: theme.colors.iconDanger,
        warning: theme.colors.iconWarning,
        success: theme.colors.iconSuccess,
        info: theme.colors.iconInfo,
      }}
    >
      {children}
    </ArcadeIconProvider>
  )
}

export const CashDesignSystemContext = React.createContext({
  imageUrlHandler: undefined as ImageUrlHandler | undefined,
  hasBeenProvidedOnce: false,
  theme: {
    darkMode: false,
    colors: {} as ThemeColors,
    arcadeColors: {} as ArcadeThemeColors,
    dropShadows: {} as ThemeDropShadows,
  },
})

/**
 * Use to change the theme variant of a component subtree.
 * To change the style at the root of an application, prefer `themeVariant` on the `CashDesignSystemProvider`.
 */
export const ThemeVariantProvider = ({
  themeVariant,
  children,
  shouldUseArcadeBackground = false,
  shouldUseArcadeBackgroundLogin = false,
}: {
  children: React.ReactNode
  themeVariant: ThemeVariant
  shouldUseArcadeBackground?: boolean
  shouldUseArcadeBackgroundLogin?: boolean
  /**
   * Allows for dark mode override when provided
   */
  darkMode?: boolean
}) => {
  const context = useContext(CashDesignSystemContext)
  const darkMode = context.theme.darkMode

  if (!context.hasBeenProvidedOnce) {
    throw new Error('`ThemeVariantProvider` must be rendered within a `CashDesignSystemProvider`')
  }

  const { component: arcadeComponentColorOverrides } = getOverrideArcadeColorsByThemeVariant({ themeVariant, darkMode })

  const theme = useMemo(
    () => ({
      ...context.theme,
      arcadeColors: {
        semantic: {
          ...context.theme.arcadeColors.semantic,
        },
        component: {
          ...context.theme.arcadeColors.component,
          ...arcadeComponentColorOverrides,
        },
      },
      colors: {
        ...context.theme.colors,
        ...getOverrideColorsByThemeVariant({ themeVariant, darkMode }),
      },
      dropShadows: getDropShadows(darkMode),
    }),
    [arcadeComponentColorOverrides, context.theme, darkMode, themeVariant]
  )

  return (
    <CashDesignSystemContext.Provider value={{ ...context, ...theme }}>
      <ThemeProvider theme={theme}>
        <ThemedArcadeIconProvider>{children}</ThemedArcadeIconProvider>
      </ThemeProvider>
    </CashDesignSystemContext.Provider>
  )
}

const CashDesignSystemProvider: React.FC<Props> = ({
  children,
  darkMode,
  bodyBackground = 'background',
  themeVariant = 'default',
  featureFlags = {},
  shouldUseArcadeBackground = false,
  shouldUseArcadeBackgroundLogin = false,
  imageUrlHandler,
}) => {
  const context = useContext(CashDesignSystemContext)

  if (context.hasBeenProvidedOnce) {
    // Tip: To override a theme variant in a subtree of your application, use the `ThemeVariantProvider` directly.
    throw new Error('The `CashDesignSystemProvider` cannot be provided more than once')
  }

  const theme = useMemo(
    () => ({
      arcadeColors: getArcadeColorsByThemeVariant({ themeVariant, darkMode }),
      colors: getColorsByThemeVariant({
        themeVariant,
        darkMode,
        shouldUseArcadeBackground,
        shouldUseArcadeBackgroundLogin,
      }),
      darkMode,
      dropShadows: getDropShadows(darkMode),
    }),
    [darkMode, shouldUseArcadeBackground, shouldUseArcadeBackgroundLogin, themeVariant]
  )

  // Note: The `what-import` module has a side effect, must include this in imported code to ensure its bundled.
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  whatInput

  return (
    <CashDesignSystemContext.Provider value={{ imageUrlHandler, theme, hasBeenProvidedOnce: true }}>
      <ThemeProvider
        theme={{
          addOpacity,
          pxToRem,
          remToPx,
          remToPxRaw,
          border,
          breakpoints,
          maxWidths,
          motion,
          selectors,
          spacing,
          typography,
          weights,
          ...theme,
        }}
      >
        <GlobalStyles bodyBackground={bodyBackground} />
        <ArcadeComponentMigrationProvider featureFlags={featureFlags}>
          <ThemedArcadeIconProvider>{children}</ThemedArcadeIconProvider>
        </ArcadeComponentMigrationProvider>
      </ThemeProvider>
    </CashDesignSystemContext.Provider>
  )
}

export default CashDesignSystemProvider
